import { useLayoutEffect, useRef } from 'react'

import { useCalendarContext } from '../context'

/**
 * Scrolls the hour grid to the current hour, once, after the items arrive.
 *
 * Moves the calendar's own scroll container with `scrollTop`. Do not use
 * `scrollIntoView()` here. It scrolls every scrollable ancestor, and
 * `overflow: hidden` does not stop a programmatic scroll. The root layout is a
 * `100dvh` grid, so on a narrow viewport the navbar and the mobile topbar
 * scroll out of the grid. The user cannot bring them back.
 *
 * @param scrollContainerRef - The element that scrolls the hour grid
 * @returns A ref for the cell that holds the current hour
 */
export const useScrollToCurrentHour = (
  scrollContainerRef: React.RefObject<HTMLDivElement>,
): React.RefObject<HTMLTableCellElement> => {
  const { items } = useCalendarContext()
  const currentHourRef = useRef<HTMLTableCellElement>(null)
  const hasScrolledRef = useRef<boolean>(false)

  useLayoutEffect(() => {
    if (!currentHourRef.current || !items || hasScrolledRef.current) {
      return
    }

    let cancelled = false
    queueMicrotask(() => {
      const container = scrollContainerRef.current
      const element = currentHourRef.current
      if (cancelled || !container || !element) {
        return
      }

      const headerHeight = container.querySelector('thead')?.clientHeight ?? 0
      const label = element.closest('tr')?.querySelector('time')
      const labelOffset = (label?.getBoundingClientRect().height ?? 0) / 2
      container.scrollTop +=
        element.getBoundingClientRect().top -
        container.getBoundingClientRect().top -
        headerHeight -
        labelOffset
      hasScrolledRef.current = true
    })

    return (): void => {
      cancelled = true
    }
  }, [items, scrollContainerRef])

  return currentHourRef
}
